import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

@ngCore.Component({
  host: {
    // TODO: Once this component is reused, this can be removed.
    'data-atn-space-variant': 'full',
  },
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.textArea.Component,
    ds.input.select.single.Component,
    ds.input.checkbox.Component,
    ds.notice.Component,
  ],
  selector: 'atn-space-room-data-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  // TODO: Possibly make an input for records collection that will allow reuse
  // between full and lite space
  /** @see {@link state.space.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /**
   * Style enum exposed to HTML
   *
   * @see {@link ents.core.display.Style}
   */
  public readonly StyleEnum = ents.core.display.Style

  /** @see {@link state.space.roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(
    state.space.roomTypes.Collection,
  )

  /** Editing record */
  public readonly recordSg = ngCore.computed(() =>
    this.recordsEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal(),
  )

  /**
   * Room type items in the form of {@link ds.input.select.models.Item} for the select.
   */
  public readonly roomTypesItemsSg = ngCore.computed(
    (
      roomTypes = this._roomTypesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      roomTypes
        .map<ds.input.select.models.Item<ents.extra.space.RoomType.Id>>(
          ({ id: value, name: label }) => ({
            label,
            value,
          }),
        )
        .filter(TypeChecker.isNotNullish.raw),
  )

  /** Items for anomalies  */
  public readonly anomalyItemsSg = ngCore.computed(() =>
    ents.extra.space.Record.Anomaly.ALL.map(anomaly => ({
      label: ents.extra.space.Record.Anomaly.getLabel(anomaly),
      value: anomaly,
    })),
  )

  /** Editing record description */
  public readonly descriptionSg = ngCore.computed(
    (record = this.recordSg()) => record?.description,
  )

  /** Editing record comment */
  public readonly commentSg = ngCore.computed(
    (record = this.recordSg()) => record?.comment,
  )

  /** Whether the current record is assigned to a cluster. */
  public readonly isRecordInClusterSg = ngCore.computed(
    (
      recordId = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal(),
      record = recordId
        ? this.recordsEc.plugins.cachePlugin.feature.selectors
            .getCachedEntity(recordId)
            .asSignal()
        : undefined,
    ) => TypeChecker.isNotNullish(record?.clusterId),
  )

  /** Editing record anomalies */
  public readonly anomaliesSg = ngCore.computed(
    (record = this.recordSg()) => record?.anomalies ?? [],
  )

  /** Editing record room type id */
  public readonly roomTypeIdSg = ngCore.computed(
    (record = this.recordSg()) => record?.roomTypeId,
  )

  /**
   * Determines if Description or Room Type has been changed
   * Shows a notice to inform users that changes will affect all scenarios
   *
   * Compares cached entity with updated entity to detect changes
   */
  public readonly showScenariosImpactNoticeSg = ngCore.computed(
    (
      recordId = this.recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal(),
    ) => {
      if (!recordId) {
        return false
      }

      const cachedRecord = this.recordsEc.plugins.cachePlugin.feature.selectors
        .getCachedEntity(recordId)
        .asSignal()

      const updatedRecord =
        this.recordsEc.plugins.editingPlugin.feature.selectors
          .getEditedEntity(recordId)
          .asSignal()

      if (!cachedRecord || !updatedRecord) {
        return false
      }

      const descriptionChanged =
        updatedRecord.description !== cachedRecord.description

      const roomTypeChanged =
        updatedRecord.roomTypeId !== cachedRecord.roomTypeId

      return descriptionChanged || roomTypeChanged
    },
  )

  /** Handling updates for anomaly on checkbox click */
  public anomalyCheckboxChangedHandler(
    selected: ents.extra.space.Record.Anomaly,
  ) {
    const current = this.anomaliesSg()

    const updated = current.includes(selected)
      ? current.filter(a => a !== selected)
      : [...current, selected]

    this.updateRecord('anomalies', updated)
  }

  /**
   * Updates a specific property of the currently edited record.
   */
  public updateRecord<K extends keyof ents.extra.space.Record.Update>(
    prop: K,
    value: ents.extra.space.Record.Update[K],
  ): void {
    this.recordsEc.plugins.editingPlugin.feature.actions.edit
      .from(`Space.Record.Modal.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: record => {
          if (!record) {
            return
          }
          record[prop] = value
        },
      })
  }

  /** Submit changes handler */
  public saveClickHandler = () => {
    this.recordsEc.plugins.editingPlugin.feature.actions.submit
      .from('RoomData.Modal.SaveButton.Clicked')
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }

  /** Discard changes handler */
  public cancelClickHandler = () => {
    this.recordsEc.plugins.editingPlugin.feature.actions.discard
      .from('RoomData.Modal.CancelButton.Clicked')
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }
}
